import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check,
  X,
  Sparkles,
  Play,
  Clock,
  Star,
  ShieldCheck,
  BadgeCheck,
  Fingerprint,
  FlaskConical,
  ArrowRight,
  MessageCircle,
  Camera,
  ClipboardList,
  Lock,
  CalendarCheck,
  RefreshCw,
} from "lucide-react";
import { goToAppStore, API } from "../lib/api";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const FIRE_HERO =
  "https://static.prod-images.emergentagent.com/jobs/ca9230b3-f03c-418c-bfeb-5a9295ae9f53/images/834bda8d506129a27ffcf740c58c6da69a010dad17b54b85ddadd41d8babd53e.jpeg";
const VIDEO_SRC = `${API}/media/how-it-works-users`;

const TRUST_BADGES = [
  "Minimum 5 years salon experience & NVQ qualified",
  "Includes 3 free visual hairstyle try on previews",
  "10 minute maximum session, most last just a few minutes",
];

const PROBLEMS = [
  "Rushed 3 minute chair consultations under bright salon lights and social pressure.",
  "Bringing celebrity Pinterest photos that don't match your hair texture or face shape.",
  "Spending £150 to £300+ on cuts or colours that leave you disappointed or damaged.",
  "Buying generic shelf products pushed by salons trying to hit weekly retail quotas.",
];

const SOLUTIONS = [
  "Dedicated protected chat from home, capped at 10 minutes with most just a few, before booking or cutting.",
  "Style DNA matching and visual previews to see what actually flatters your face shape.",
  "Independent, unbiased expert validation, so you know what is chemically achievable before spending hundreds.",
  "3 custom retail recommendations curated specifically for your hair texture and scalp needs.",
];

const STEPS = [
  {
    n: "01",
    icon: Sparkles,
    title: "Complete your Style DNA",
    desc: "Answer a 2 minute quiz covering hair history, texture, scalp condition and styling habits.",
  },
  {
    n: "02",
    icon: Camera,
    title: "Preview your styles",
    desc: "Upload your photo and use your 3 free visual try ons to explore cuts and shapes.",
  },
  {
    n: "03",
    icon: MessageCircle,
    title: "Instant match & live chat",
    desc: "Match with a top rated verified Senior Stylist for a dedicated real time consultation, capped at a 10 minute maximum.",
  },
  {
    n: "04",
    icon: ClipboardList,
    title: "Your action plan & products",
    desc: "Walk away with precise salon instructions and 3 personalised product recommendations.",
  },
];

const DELIVERABLES = [
  {
    title: "2 one-on-one message-based consultations",
    desc: "Two private message-based consultations with a verified Senior Stylist (NVQ Level 2/3 or equivalent, 5+ years experience), each with up to 10 minutes of real time chat, though most last just a few.",
  },
  {
    title: "Complete Style DNA profile",
    desc: "Lifetime access to your hair profile, porosity score and diagnostic history.",
  },
  {
    title: "3 free visual hairstyle try ons",
    desc: "Photorealistic preview technology to test haircuts and shapes on your own photo.",
  },
  {
    title: "Personalised product recommendations",
    desc: "3 targeted, professional grade products matched specifically to your chat and hair type.",
  },
  {
    title: "Salon ready brief",
    desc: "The exact terminology, formulas and questions to hand your in person colourist or barber.",
  },
];

const DNA_FEATURES = [
  {
    icon: Fingerprint,
    title: "Texture & density precision",
    desc: "Whether Type 1A straight or 4C coily, get advice from stylists who specialise in your specific curl pattern.",
  },
  {
    icon: FlaskConical,
    title: "Chemical history audit",
    desc: "Prevents catastrophic bleaching or perming mistakes by accounting for previous box dyes, toners and heat exposure.",
  },
  {
    icon: Clock,
    title: "Lifestyle & maintenance realism",
    desc: "Tells you truthfully how many minutes of morning styling a haircut needs before you commit to the scissors.",
  },
];

const TRUST = [
  {
    icon: ShieldCheck,
    title: "Strictly vetted",
    desc: "Under 20% of applicants pass our verification criteria: NVQ qualifications, 5+ years salon experience and reference vetting.",
  },
  {
    icon: BadgeCheck,
    title: "Unbiased advice",
    desc: "Our stylists do not push unnecessary £80 salon treatments, they give honest, client first advice.",
  },
  {
    icon: Star,
    title: "Rebook top matches",
    desc: "Rate your stylist 5 stars to keep them prioritised in your top 5 recommended matches for future check ins.",
  },
];

const FAQS = [
  {
    q: "How long is a consultation?",
    a: "Every consultation is capped at a maximum of 10 minutes, but most only last a few minutes. Your stylist reviews your Style DNA and photos beforehand, so the time is focused purely on answering your questions and building your hair plan.",
  },
  {
    q: "Can I show the consultation notes to my salon stylist?",
    a: "Yes. Your consultation leaves you with clear professional guidance and terminology you can show straight to your local stylist or colourist.",
  },
  {
    q: "What if I need product recommendations?",
    a: "Your stylist curates 3 professional products during your chat that match your exact texture and routine, available for direct checkout in our store.",
  },
];

const JOURNEY_STEPS = [
  {
    n: "01",
    icon: Camera,
    title: "Baseline Assessment",
    desc: "Upload scalp, root, and damage photos to establish your hair's starting health profile.",
  },
  {
    n: "02",
    icon: FlaskConical,
    title: "Targeted Regimen",
    desc: "Receive tailored product recommendations, wash routines, and application advice from a verified senior stylist.",
  },
  {
    n: "03",
    icon: CalendarCheck,
    title: "30-Day Health Check",
    desc: "Check in every 30 days to review visible progress, adjust formulas, and adapt to seasonal changes.",
  },
];

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

function CTAButton({ label = "Get Started for £12", testid, dark = true, className = "" }) {
  return (
    <button
      onClick={goToAppStore}
      data-testid={testid}
      className={
        (dark
          ? "bg-stylist-ink text-white hover:bg-stylist-gold"
          : "bg-stylist-gold text-stylist-ink hover:bg-stylist-ink hover:text-white") +
        " group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-medium transition-colors duration-300 " +
        className
      }
    >
      {label}
      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
    </button>
  );
}

export default function UserLanding() {
  const [showBar, setShowBar] = useState(false);

  useEffect(() => {
    document.title = "Book a Consultation | Senior Stylist";
    window.scrollTo(0, 0);
    const onScroll = () => setShowBar(window.scrollY > 640);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToVideo = () =>
    document.getElementById("how")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-stylist-bg text-stylist-ink" data-testid="user-landing-page">
      {/* Slim sticky header */}
      <header className="sticky top-0 z-40 backdrop-blur bg-stylist-bg/80 border-b border-stylist-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <span className="w-9 h-9 rounded-full bg-white border border-stylist-border flex items-center justify-center">
              <img src="/logo.png" alt="Senior Stylist" className="w-6 h-6 object-contain" />
            </span>
            <span className="font-serif text-lg">Senior Stylist</span>
          </Link>
          <button
            onClick={goToAppStore}
            data-testid="header-cta"
            className="rounded-full bg-stylist-ink text-white px-5 py-2.5 text-xs font-medium hover:bg-stylist-gold transition-colors"
          >
            Get Started for £12
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="user-hero">
        <div className="absolute inset-0">
          <img src={FIRE_HERO} alt="A luxury hairbrush with match tipped bristles, one lit, on a dark vanity" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-stylist-ink/95 via-stylist-ink/80 to-stylist-ink/35" />
          <div className="absolute inset-0 ss-grain opacity-[0.08] pointer-events-none" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 md:py-32 lg:py-40">
          <div className="max-w-2xl text-white">
            <p className="font-mono text-[0.62rem] sm:text-[0.68rem] uppercase tracking-[0.24em] text-stylist-gold">
              Founding member trial · 1 on 1 senior stylist consultation
            </p>

            <h1 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight">
              Playing with fire?{" "}
              <span className="italic text-stylist-gold">A flawed hair routine is a disaster waiting to ignite.</span>
            </h1>

            <p className="mt-6 text-base md:text-lg text-white/75 max-w-xl leading-relaxed">
              Your hair deserves better than guesswork. Join our exclusive Founding Member trial
              and gain clarity and precision from real, vetted Senior Stylists worldwide, before anyone
              touches your hair with bleach or scissors.
            </p>

            <div
              className="mt-7 inline-flex items-center gap-3 rounded-2xl border border-stylist-gold/40 bg-stylist-gold/10 backdrop-blur px-5 py-3"
              data-testid="user-offer-badge"
            >
              <Sparkles className="w-5 h-5 text-stylist-gold shrink-0" />
              <span className="text-sm text-white">
                <span className="font-semibold text-stylist-gold">Free early access:</span>{" "}
                2 free hair consultations for Founding Members
              </span>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                onClick={goToAppStore}
                data-testid="hero-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-8 py-4 text-sm font-medium hover:bg-white transition-colors duration-300"
              >
                Become a Founding Member
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={scrollToVideo}
                data-testid="hero-see-how"
                className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white"
              >
                <Play className="w-4 h-4 text-stylist-gold" />
                See how it works (60 seconds)
              </button>
            </div>

            <ul className="mt-10 space-y-2.5">
              {TRUST_BADGES.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-white/85">
                  <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-stylist-gold/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-stylist-gold" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROBLEM vs SOLUTION */}
      <section className="py-16 md:py-24 bg-stylist-bg2">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-14">
            <span className="eyebrow">The difference</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">
              Stop gambling in the salon chair
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <motion.div
              {...reveal}
              className="rounded-2xl border border-stylist-border bg-white/50 p-8"
              data-testid="problem-card"
            >
              <h3 className="font-serif text-2xl text-stylist-ink2">The traditional salon gamble</h3>
              <ul className="mt-6 space-y-4">
                {PROBLEMS.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-stylist-ink2">
                    <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-stylist-ink/5 flex items-center justify-center">
                      <X className="w-3 h-3 text-stylist-muted" />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.1 }}
              className="rounded-2xl border-2 border-stylist-gold/40 bg-white p-8 shadow-xl relative"
              data-testid="solution-card"
            >
              <span className="absolute -top-3 left-8 rounded-full bg-stylist-gold text-stylist-ink px-3 py-1 text-[10px] font-mono uppercase tracking-widest">
                Senior Stylist
              </span>
              <h3 className="font-serif text-2xl text-stylist-ink">The Senior Stylist experience</h3>
              <ul className="mt-6 space-y-4">
                {SOLUTIONS.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-sm text-stylist-ink">
                    <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                      <Check className="w-3 h-3 text-stylist-gold" />
                    </span>
                    {s}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VIDEO WALKTHROUGH */}
      <section id="how" className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-12">
            <span className="eyebrow">Watch a 60 second tour</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">How it works</h2>
            <p className="mt-4 text-stylist-ink2">
              Discover how simple it is to match with a dedicated Senior Stylist, diagnose your
              hair needs, and get the look you actually want.
            </p>
          </motion.div>

          <motion.div
            {...reveal}
            className="relative rounded-2xl overflow-hidden border border-stylist-border shadow-xl aspect-video bg-stylist-ink"
            data-testid="video-walkthrough"
          >
            <video
              className="absolute inset-0 w-full h-full object-cover"
              controls
              playsInline
              preload="auto"
              data-testid="video-player"
            >
              <source src={VIDEO_SRC} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </motion.div>
          <p className="mt-4 text-center text-sm text-stylist-muted">
            How to take your Style DNA quiz, preview 3 styles, and chat directly with an expert stylist.
          </p>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.08 }}
                className="bg-white border border-stylist-border rounded-2xl p-6 hover:border-stylist-gold/40 hover:shadow-lg transition-all duration-500"
                data-testid={`step-${s.n}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs tracking-[0.3em] text-stylist-muted">{s.n}</span>
                  <div className="w-10 h-10 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-stylist-gold" />
                  </div>
                </div>
                <h3 className="mt-4 font-serif text-lg leading-snug">{s.title}</h3>
                <p className="mt-2 text-sm text-stylist-ink2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 30-DAY HAIR HEALTH JOURNEY */}
      <section className="py-16 md:py-24 bg-stylist-bg2" data-testid="journey-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-14">
            <span className="eyebrow">The 30-Day Hair Health Journey</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
              Continuous Professional Care.{" "}
              <span className="italic text-stylist-gold">Not a One-Off Quick Fix.</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-stylist-ink2 leading-relaxed">
              Track your hair health, scalp recovery, and growth progress with verified stylists every 30 days.
            </p>
          </motion.div>

          {/* Visual progression loop */}
          <div className="relative grid md:grid-cols-3 gap-6 lg:gap-8">
            {JOURNEY_STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.1 }}
                className="relative bg-white border border-stylist-border rounded-2xl p-7 hover:border-stylist-gold/40 hover:shadow-xl transition-all duration-500"
                data-testid={`journey-step-${s.n}`}
              >
                <div className="flex items-center justify-between">
                  <span className="w-12 h-12 rounded-full bg-stylist-gold/12 flex items-center justify-center">
                    <s.icon className="w-6 h-6 text-stylist-gold" />
                  </span>
                  <span className="font-serif text-4xl text-stylist-gold/25 leading-none">{s.n}</span>
                </div>
                <h3 className="mt-5 font-serif text-xl leading-snug">{s.title}</h3>
                <p className="mt-3 text-sm text-stylist-ink2 leading-relaxed">{s.desc}</p>
                {i < JOURNEY_STEPS.length - 1 && (
                  <span className="hidden md:flex absolute top-1/2 -right-4 lg:-right-5 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-stylist-ink text-white items-center justify-center shadow-lg">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </motion.div>
            ))}
          </div>

          {/* Loop-back cue */}
          <motion.div {...reveal} className="mt-6 flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-stylist-muted" data-testid="journey-loop-cue">
            <RefreshCw className="w-3.5 h-3.5 text-stylist-gold" />
            The loop repeats every 30 days, so your hair keeps improving
          </motion.div>

          {/* Conversion callout */}
          <motion.div
            {...reveal}
            className="mt-12 rounded-3xl border border-stylist-gold/30 bg-stylist-ink text-white p-8 sm:p-10 text-center relative overflow-hidden"
            data-testid="journey-callout"
          >
            <div className="absolute inset-0 ss-grain opacity-[0.06] pointer-events-none" />
            <div
              className="absolute inset-0 opacity-40 pointer-events-none"
              style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(197,155,39,0.22), transparent 60%)" }}
            />
            <div className="relative">
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[11px] sm:text-xs font-mono uppercase tracking-[0.16em] text-stylist-gold" data-testid="journey-trust-badge">
                <span className="inline-flex items-center gap-1.5"><CalendarCheck className="w-3.5 h-3.5" /> Tracked Over Time</span>
                <span className="text-white/30">&bull;</span>
                <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5" /> Verified Senior Stylists</span>
                <span className="text-white/30">&bull;</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> 10-Minute Instant Check-ins</span>
              </div>
              <h3 className="mt-6 font-serif text-2xl sm:text-3xl leading-snug max-w-xl mx-auto">
                Your hair health, reviewed and refined month after month.
              </h3>
              <div className="mt-8 flex justify-center">
                <button
                  onClick={goToAppStore}
                  data-testid="journey-cta"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-9 py-4 text-sm font-medium hover:bg-white transition-colors duration-300"
                >
                  Start Your 30-Day Journey
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* £12 PACKAGE */}
      <section className="py-16 md:py-24 bg-stylist-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 ss-grain opacity-[0.06] pointer-events-none" />
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(197,155,39,0.25), transparent 60%)" }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-8">
          <motion.div {...reveal} className="text-center mb-10">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">
              One simple monthly membership
            </span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">
              What's included in your £12 a month membership
            </h2>
          </motion.div>

          <motion.div
            {...reveal}
            className="rounded-3xl border border-white/12 bg-white/[0.04] backdrop-blur p-8 sm:p-10"
            data-testid="package-card"
          >
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-white/10 pb-6">
              <div>
                <h3 className="font-serif text-2xl">Senior Stylist Membership</h3>
                <p className="mt-2 text-sm text-white/60 max-w-md">
                  Everything you need for total hair clarity, confidence and expert guidance.
                </p>
              </div>
              <div className="text-right">
                <span className="font-serif text-5xl text-stylist-gold">£12</span>
                <span className="text-lg text-white/60 ml-1">plus VAT</span>
                <p className="text-[11px] font-mono uppercase tracking-widest text-white/40 mt-1">
                  per month · cancel anytime
                </p>
              </div>
            </div>

            <ul className="mt-8 space-y-5">
              {DELIVERABLES.map((d) => (
                <li key={d.title} className="flex items-start gap-4">
                  <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-stylist-gold/20 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-stylist-gold" />
                  </span>
                  <div>
                    <p className="text-sm font-medium">{d.title}</p>
                    <p className="text-sm text-white/55 mt-0.5 leading-relaxed">{d.desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            <button
              onClick={goToAppStore}
              data-testid="package-cta"
              className="mt-9 w-full group inline-flex items-center justify-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-8 py-4 text-sm font-medium hover:bg-white transition-colors duration-300"
            >
              Start your £12 a month membership
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="mt-4 flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-widest text-white/40">
              <Lock className="w-3 h-3" />
              Safe & encrypted checkout
            </p>
          </motion.div>
        </div>
      </section>

      {/* STYLE DNA ADVANTAGE */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="max-w-2xl mb-14">
            <span className="eyebrow">Precision, not guesswork</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">The Style DNA advantage</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {DNA_FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.08 }}
                className="rounded-2xl border border-stylist-border bg-white p-8 hover:shadow-lg transition-shadow duration-500"
                data-testid={`dna-${i + 1}`}
              >
                <div className="w-12 h-12 rounded-full bg-stylist-gold/12 flex items-center justify-center">
                  <f.icon className="w-6 h-6 text-stylist-gold" />
                </div>
                <h3 className="mt-5 font-serif text-xl leading-snug">{f.title}</h3>
                <p className="mt-3 text-sm text-stylist-ink2 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STANDARDS */}
      <section className="py-16 md:py-24 bg-stylist-bg2">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <motion.div {...reveal} className="lg:col-span-4">
              <span className="eyebrow">Trust standards</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Verified stylist credibility
              </h2>
              <div className="mt-8 rounded-2xl bg-stylist-ink text-white p-7">
                <span className="font-serif text-5xl text-stylist-gold">&lt;20%</span>
                <p className="mt-2 text-sm text-white/60">
                  of applicants pass our strict verification to become a Senior Stylist.
                </p>
              </div>
            </motion.div>

            <div className="lg:col-span-8 grid sm:grid-cols-1 gap-5">
              {TRUST.map((t, i) => (
                <motion.div
                  key={t.title}
                  {...reveal}
                  transition={{ ...reveal.transition, delay: i * 0.08 }}
                  className="flex items-start gap-5 rounded-2xl border border-stylist-border bg-white p-7"
                  data-testid={`trust-${i + 1}`}
                >
                  <div className="w-12 h-12 shrink-0 rounded-full bg-stylist-gold/12 flex items-center justify-center">
                    <t.icon className="w-6 h-6 text-stylist-gold" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl">{t.title}</h3>
                    <p className="mt-1.5 text-sm text-stylist-ink2 leading-relaxed">{t.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <motion.div {...reveal} className="text-center mb-10">
            <span className="eyebrow">Good to know</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">Frequently asked questions</h2>
          </motion.div>
          <motion.div {...reveal}>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-stylist-border">
                  <AccordionTrigger className="font-serif text-lg text-stylist-ink hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-stylist-ink2 leading-relaxed text-sm">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* BOTTOM ANCHOR */}
      <section className="py-20 md:py-28 bg-stylist-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 ss-grain opacity-[0.06] pointer-events-none" />
        <div
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 120%, rgba(197,155,39,0.22), transparent 60%)" }}
        />
        <motion.div {...reveal} className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Never walk out of a salon regretting your hair again.
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Join thousands of clients getting expert, independent guidance from senior stylists
            who care about your hair health.
          </p>
          <div className="mt-10 flex justify-center">
            <button
              onClick={goToAppStore}
              data-testid="bottom-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-9 py-4 text-sm font-medium hover:bg-white transition-colors duration-300"
            >
              Start your consultation, only £12 a month
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <p className="mt-10 text-[11px] font-mono uppercase tracking-[0.22em] text-white/40">
            Senior Stylist Ltd. · Safe &amp; encrypted checkout · Verified hairdressing experts worldwide
          </p>
        </motion.div>
      </section>

      {/* STICKY BOTTOM BAR */}
      <div
        className={
          "fixed bottom-0 inset-x-0 z-40 transition-transform duration-500 " +
          (showBar ? "translate-y-0" : "translate-y-full")
        }
        data-testid="sticky-cta-bar"
      >
        <div className="mx-auto max-w-3xl m-3 rounded-2xl bg-white border border-stylist-border shadow-2xl px-5 py-3 flex items-center justify-between gap-4">
          <div className="leading-tight">
            <p className="text-sm font-medium text-stylist-ink">£12 plus VAT / month membership</p>
            <p className="text-xs text-stylist-muted">
              2 consultations, 10 min max chat each, 3 try ons and a salon brief
            </p>
          </div>
          <button
            onClick={goToAppStore}
            data-testid="sticky-cta-button"
            className="shrink-0 rounded-full bg-stylist-ink text-white px-6 py-3 text-sm font-medium hover:bg-stylist-gold transition-colors"
          >
            Get Started · £12
          </button>
        </div>
      </div>
    </div>
  );
}
